import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Eye,
  EyeOff,
  KeyRound,
  Link2,
  Loader2,
  LogOut,
  Mail,
  User,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  UserCircle,
} from 'lucide-react';
import { Form, Modal, Button } from 'react-bootstrap';

import axiosInstance from '../../api/api';
import AppBackButton from '../common/AppBackButton';
import { deactivateUserAccount, deleteUserAccount } from '../Preview/api';
import { API_ROUTES } from '../../config/api-routes';
import './Settings.css';

type CurrentUserResponse = {
  user?: {
    id?: string | number;
    email?: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    usertype?: string;
    isUserVerified?: boolean;
    is_verified?: boolean;
    authProvider?: string;
    auth_provider?: string;
    hasUsablePassword?: boolean;
    dateJoined?: string | null;
    date_joined?: string | null;
    profileImg?: string;
  };
};

type CurrentUser = NonNullable<CurrentUserResponse['user']>;

function formatJoinDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

function getInitials(name: string, email: string): string {
  const fromName = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');
  if (fromName) return fromName;
  return (email[0] ?? '?').toUpperCase();
}

const PASSWORD_MIN_LENGTH = 8;

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSubmitting, setPwSubmitting] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteMode, setDeleteMode] = useState<'delete' | 'deactivate'>('deactivate');
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await axiosInstance.get<CurrentUserResponse>('user/current-user');
        if (!active) return;
        setUser(response.data.user || (response.data as unknown as CurrentUser));
      } catch (err) {
        console.error('Failed to load current user', err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const fullName = useMemo(() => {
    if (!user) return '';
    return [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  }, [user]);

  const role = user?.role || user?.usertype || 'user';
  const isVerified = Boolean(user?.isUserVerified ?? user?.is_verified);
  const authProvider = (user?.authProvider || user?.auth_provider || 'email').toLowerCase();
  const hasUsablePassword = authProvider === 'email';
  const dateJoined = user?.dateJoined || user?.date_joined;

  const newPasswordHints = useMemo(() => {
    return {
      length: newPassword.length >= PASSWORD_MIN_LENGTH,
      different: newPassword.length > 0 && newPassword !== currentPassword,
      match: confirmPassword.length > 0 && confirmPassword === newPassword,
    };
  }, [newPassword, currentPassword, confirmPassword]);

  const canSubmitPassword =
    hasUsablePassword &&
    currentPassword.length > 0 &&
    newPasswordHints.length &&
    newPasswordHints.different &&
    newPasswordHints.match &&
    !pwSubmitting;

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwError(null);
    if (!canSubmitPassword) return;

    setPwSubmitting(true);
    try {
      await axiosInstance.post(API_ROUTES.AUTH.CHANGE_PASSWORD, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
    } catch (err: unknown) {
      const message = extractApiError(err) || 'Could not change password.';
      setPwError(message);
      toast.error(message);
    } finally {
      setPwSubmitting(false);
    }
  }

  function handleForgotPasswordLink() {
    navigate('/forgot-password');
  }

  function handleLogout() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    navigate('/', { replace: true });
  }

  async function handleConfirmDelete() {
    try {
      setIsDeleting(true);
      if (deleteMode === 'delete') {
        if (confirmText !== 'DELETE') {
          toast.warn('Type DELETE to confirm.');
          return;
        }
        await deleteUserAccount();
        toast.success('Account deleted.');
      } else {
        await deactivateUserAccount();
        toast.success('Account deactivated.');
      }
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      navigate('/', { replace: true });
    } catch (err) {
      console.error(err);
      toast.error('Action failed. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="us-page">
      <div className="us-page-inner">
        <AppBackButton />

        <header className="us-header">
          <h1>Account Settings</h1>
          <p>Manage your account, security, and access preferences.</p>
        </header>

        <nav className="us-card us-quick-nav" aria-label="Account sections">
          <button
            type="button"
            className="us-quick-nav-item"
            onClick={() => navigate('/user-profile')}
          >
            <span className="us-quick-nav-icon" aria-hidden>
              <User size={18} />
            </span>
            <span className="us-quick-nav-text">
              <strong>Profile customization</strong>
              <span>Edit your public profile, photo, and prompts</span>
            </span>
            <ChevronRight size={18} aria-hidden />
          </button>
          <button
            type="button"
            className="us-quick-nav-item"
            onClick={() => navigate('/account/connections')}
          >
            <span className="us-quick-nav-icon" aria-hidden>
              <Link2 size={18} />
            </span>
            <span className="us-quick-nav-text">
              <strong>Connected accounts</strong>
              <span>Google, LinkedIn, and Facebook sign-in</span>
            </span>
            <ChevronRight size={18} aria-hidden />
          </button>
        </nav>

        {loading ? (
          <div className="us-card us-loading">
            <Loader2 className="us-spin" size={20} />
            <span>Loading your account…</span>
          </div>
        ) : (
          <>
            <section className="us-card us-account">
              <div className="us-avatar" aria-hidden>
                {user?.profileImg ? (
                  <img src={user.profileImg} alt="" />
                ) : (
                  <span>{getInitials(fullName, user?.email || '')}</span>
                )}
              </div>
              <div className="us-account-info">
                <div className="us-account-name-row">
                  <h2>{fullName || 'Your account'}</h2>
                  <span className={`us-pill ${isVerified ? 'us-pill--ok' : 'us-pill--warn'}`}>
                    {isVerified ? (
                      <>
                        <ShieldCheck size={13} aria-hidden /> Verified
                      </>
                    ) : (
                      <>
                        <ShieldAlert size={13} aria-hidden /> Unverified
                      </>
                    )}
                  </span>
                </div>
                <p className="us-account-email">
                  <Mail size={14} aria-hidden />
                  {user?.email || '—'}
                </p>
                <div className="us-meta-grid">
                  <div>
                    <span className="us-meta-label">Account type</span>
                    <strong className="us-meta-value">
                      {role === 'recruiter' ? 'Recruiter' : 'Candidate'}
                    </strong>
                  </div>
                  <div>
                    <span className="us-meta-label">Sign-in method</span>
                    <strong className="us-meta-value">{authProviderLabel(authProvider)}</strong>
                  </div>
                  <div>
                    <span className="us-meta-label">Member since</span>
                    <strong className="us-meta-value">{formatJoinDate(dateJoined)}</strong>
                  </div>
                </div>
              </div>
            </section>

            <section className="us-card us-security">
              <div className="us-section-head">
                <div className="us-section-head-icon">
                  <KeyRound size={18} />
                </div>
                <div>
                  <h2>Change password</h2>
                  <p>
                    Use a strong password you don't reuse anywhere else. Your new password takes
                    effect immediately.
                  </p>
                </div>
              </div>

              {hasUsablePassword ? (
                <form onSubmit={handleChangePassword} className="us-pw-form">
                  <PasswordField
                    id="current-password"
                    label="Current password"
                    value={currentPassword}
                    onChange={setCurrentPassword}
                    autoComplete="current-password"
                    show={showCurrent}
                    onToggleShow={() => setShowCurrent((s) => !s)}
                  />
                  <PasswordField
                    id="new-password"
                    label="New password"
                    value={newPassword}
                    onChange={setNewPassword}
                    autoComplete="new-password"
                    show={showNew}
                    onToggleShow={() => setShowNew((s) => !s)}
                  />
                  <PasswordField
                    id="confirm-password"
                    label="Confirm new password"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    autoComplete="new-password"
                    show={showConfirm}
                    onToggleShow={() => setShowConfirm((s) => !s)}
                  />

                  <ul className="us-pw-requirements">
                    <Requirement
                      met={newPasswordHints.length}
                      label={`At least ${PASSWORD_MIN_LENGTH} characters`}
                    />
                    <Requirement
                      met={newPasswordHints.different}
                      label="Different from your current password"
                    />
                    <Requirement
                      met={newPasswordHints.match}
                      label="Both new password fields match"
                    />
                  </ul>

                  {pwError && (
                    <div className="us-inline-error" role="alert">
                      <AlertTriangle size={14} aria-hidden />
                      {pwError}
                    </div>
                  )}

                  <div className="us-pw-actions">
                    <button
                      type="button"
                      className="us-btn us-btn-link"
                      onClick={handleForgotPasswordLink}
                    >
                      Forgot your current password?
                    </button>
                    <button
                      type="submit"
                      className="us-btn us-btn-primary"
                      disabled={!canSubmitPassword}
                    >
                      {pwSubmitting ? (
                        <>
                          <Loader2 className="us-spin" size={16} /> Saving…
                        </>
                      ) : (
                        'Update password'
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="us-info-banner">
                  <UserCircle size={18} aria-hidden />
                  <div>
                    <strong>Password change unavailable.</strong>
                    <p>
                      Your account signs in with <strong>{authProviderLabel(authProvider)}</strong>.
                      Manage your password from your {authProviderLabel(authProvider)} account.
                    </p>
                  </div>
                </div>
              )}
            </section>

            <section className="us-card us-danger">
              <div className="us-section-head">
                <div className="us-section-head-icon us-section-head-icon--danger">
                  <AlertTriangle size={18} />
                </div>
                <div>
                  <h2>Account actions</h2>
                  <p>Sign out, deactivate, or permanently delete your account.</p>
                </div>
              </div>

              <div className="us-danger-list">
                <div className="us-danger-row">
                  <div>
                    <strong>Sign out of this device</strong>
                    <p>You can sign back in any time.</p>
                  </div>
                  <button type="button" className="us-btn us-btn-outline" onClick={handleLogout}>
                    <LogOut size={15} aria-hidden /> Sign out
                  </button>
                </div>
                <div className="us-danger-row">
                  <div>
                    <strong>Deactivate or delete account</strong>
                    <p>
                      Deactivating hides your profile and blocks sign-in. Deletion is permanent.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="us-btn us-btn-danger"
                    onClick={() => {
                      setDeleteMode('deactivate');
                      setConfirmText('');
                      setShowDeleteModal(true);
                    }}
                  >
                    <Trash2 size={15} aria-hidden /> Manage account
                  </button>
                </div>
              </div>
            </section>
          </>
        )}
      </div>

      <Modal
        show={showDeleteModal}
        onHide={() => (isDeleting ? null : setShowDeleteModal(false))}
        centered
      >
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>
            {deleteMode === 'delete' ? 'Delete account' : 'Deactivate account'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Form.Check
              type="radio"
              id="delete-mode-deactivate"
              name="delete-mode"
              label="Deactivate (can be reactivated later)"
              checked={deleteMode === 'deactivate'}
              onChange={() => setDeleteMode('deactivate')}
            />
            <div className="text-muted small ms-4 mb-2">
              Your profile becomes invisible and logins are blocked. Data is retained.
            </div>
            <Form.Check
              type="radio"
              id="delete-mode-delete"
              name="delete-mode"
              label="Delete permanently"
              checked={deleteMode === 'delete'}
              onChange={() => setDeleteMode('delete')}
            />
            <div className="text-muted small ms-4">
              Irreversible. Your profile, uploads, and personal data will be removed. Some records
              may be retained where legally required or anonymised.
            </div>
          </div>
          {deleteMode === 'delete' && (
            <>
              <div className="border rounded p-2 bg-light mb-2 small">
                To confirm, type <strong>DELETE</strong> below.
              </div>
              <Form.Control
                type="text"
                placeholder="Type DELETE to confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant={deleteMode === 'delete' ? 'danger' : 'warning'}
            onClick={() => void handleConfirmDelete()}
            disabled={isDeleting || (deleteMode === 'delete' && confirmText !== 'DELETE')}
          >
            {isDeleting ? (
              <>
                <Loader2 className="us-spin me-1" size={14} />
                Working…
              </>
            ) : deleteMode === 'delete' ? (
              'Delete account'
            ) : (
              'Deactivate'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

type PasswordFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
  show: boolean;
  onToggleShow: () => void;
};

const PasswordField: React.FC<PasswordFieldProps> = ({
  id,
  label,
  value,
  onChange,
  autoComplete,
  show,
  onToggleShow,
}) => (
  <div className="us-field">
    <label htmlFor={id}>{label}</label>
    <div className="us-input-wrap">
      <input
        id={id}
        type={show ? 'text' : 'password'}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className="us-input-toggle"
        onClick={onToggleShow}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  </div>
);

const Requirement: React.FC<{ met: boolean; label: string }> = ({ met, label }) => (
  <li className={met ? 'is-met' : ''}>
    <CheckCircle2 size={13} aria-hidden />
    {label}
  </li>
);

function authProviderLabel(provider: string): string {
  switch (provider) {
    case 'google':
      return 'Google';
    case 'linkedin':
      return 'LinkedIn';
    case 'facebook':
      return 'Facebook';
    case 'email':
    default:
      return 'Email & password';
  }
}

function extractApiError(err: unknown): string | null {
  if (typeof err === 'object' && err !== null) {
    const maybeAxios = err as {
      response?: { data?: unknown };
      message?: string;
    };
    const data = maybeAxios.response?.data;
    if (typeof data === 'string') return data;
    if (typeof data === 'object' && data !== null) {
      const rec = data as Record<string, unknown>;
      if (typeof rec.detail === 'string') return rec.detail;
      if (typeof rec.message === 'string') return rec.message;
      const firstField = Object.entries(rec).find(
        ([, value]) =>
          typeof value === 'string' || (Array.isArray(value) && typeof value[0] === 'string')
      );
      if (firstField) {
        const [, val] = firstField;
        if (Array.isArray(val)) return String(val[0]);
        return String(val);
      }
    }
    if (typeof maybeAxios.message === 'string') return maybeAxios.message;
  }
  return null;
}

export default Settings;
